
import React, { useMemo, useState, useEffect } from 'react';
import { SimulationConfig, BreakSoundZone, BallSoundEffect, BallCollisionSoundType, BreakSoundZonePreset } from '@/types';
import { SectionLabel } from './SectionLabel';
import { Toggle } from './Toggle';
import { AUDIO } from '@/services/AudioService';
import { safeGetItem, safeSetItem } from '@/lib/storage';

interface EscapeAudioConfigProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const EscapeAudioConfig: React.FC<EscapeAudioConfigProps> = ({ config, setConfig }) => {
  const [presetName, setPresetName] = useState('');
  const [savedPresets, setSavedPresets] = useState<BreakSoundZonePreset[]>([]);

  useEffect(() => {
    const loaded = safeGetItem('hypnotic_break_presets');
    if (loaded) {
        try {
            setSavedPresets(JSON.parse(loaded));
        } catch (e) {
            console.error("Failed to load presets");
        }
    }
  }, []);

  const savePreset = async () => {
    if (!presetName.trim()) return alert("Enter a name for the preset");
    const newPreset: BreakSoundZonePreset = {
        id: Math.random().toString(36).substring(7),
        name: presetName.trim(),
        zones: config.breakSoundZones
    };
    const updated = [...savedPresets, newPreset];
    setSavedPresets(updated);
    await safeSetItem('hypnotic_break_presets', JSON.stringify(updated));
    setPresetName('');
    alert("Preset saved!");
  };

  const loadPreset = (preset: BreakSoundZonePreset) => {
    if (window.confirm(`Load preset "${preset.name}"? Current zones will be replaced.`)) {
        setConfig(p => ({ ...p, breakSoundZones: preset.zones }));
    }
  };

  const deletePreset = async (id: string) => {
    if (window.confirm("Delete this preset?")) {
        const updated = savedPresets.filter(p => p.id !== id);
        setSavedPresets(updated);
        await safeSetItem('hypnotic_break_presets', JSON.stringify(updated));
    }
  };

  const soundEffects: BallSoundEffect[] = [
    'Pop', 'Wood', 'Metal', 'Synth-Zap', 'Crystal', 'Soft-Thud', 'Explosion',
    'Laser-Gun', 'Pistol', 'Sniper', 'Glass-Break', 'Digital-Beep', 
    'Retro-Jump', 'Thunder', 'Bubble', 'Camera', 'Sword', 
    'Robot', 'Alien', 'Bass-Drop', 'Cowbell', 'Snare', 
    'Kick', 'Hi-Hat', 'Cyber-Pulse', 'Magic', 'Sparkle', 
    'Steam', 'Electric', 'Glitch', 'Heavy-Slam', 
    'Echo-Pluck', 'Sub-Boom', 'Whip', 'SciFi-Door', 'Wood-Block', 'Marimba'
  ];

  // Derived sorted list for display
  const sortedZones = useMemo(() => {
    return [...config.breakSoundZones].sort((a, b) => a.threshold - b.threshold);
  }, [config.breakSoundZones]);

  const addZone = () => {
    setConfig(prev => {
        const maxThreshold = prev.breakSoundZones.reduce((acc, z) => Math.max(acc, z.threshold), 0);
        // Ensure we always have room, capped at 100
        const nextThreshold = Math.min(100, maxThreshold + 20);
        
        const newZone: BreakSoundZone = {
            id: Math.random().toString(36).substring(7),
            threshold: nextThreshold,
            soundType: 'SOUND_EFFECT',
            soundEffect: 'Explosion'
        };
        return { ...prev, breakSoundZones: [...prev.breakSoundZones, newZone] };
    });
  };

  const removeZone = (id: string) => {
    setConfig(prev => ({
        ...prev,
        breakSoundZones: prev.breakSoundZones.filter(z => z.id !== id)
    }));
  };

  const updateZone = (id: string, updates: Partial<BreakSoundZone>) => {
    setConfig(prev => ({
        ...prev,
        breakSoundZones: prev.breakSoundZones.map(z => z.id === id ? { ...z, ...updates } : z)
    }));
  };

  const playPreview = (zone: BreakSoundZone) => {
      if (zone.soundType === 'SOUND_EFFECT') {
          AUDIO.playSoundEffect(zone.soundEffect, config, 0.5);
      } else if (zone.soundType === 'CUSTOM' && zone.customSoundId) {
          const sound = config.userSounds.find(s => s.id === zone.customSoundId);
          if (sound) {
              const src = sound.url || sound.base64;
              if (src) AUDIO.playCustomBuffer(src, 0.5, config);
          }
      } else {
          AUDIO.playNote(config.selectedMelody, config, 0.5);
      }
  };

  // Visualizer Render
  const renderVisualizer = () => {
      if (sortedZones.length === 0) return (
          <div className="w-full h-4 rounded-full bg-neutral-500/20 text-[8px] flex items-center justify-center opacity-50">Global Sound Only</div>
      );

      let prevThreshold = 0;
      const bars = sortedZones.map((z, idx) => {
          const width = Math.max(0, z.threshold - prevThreshold);
          const start = prevThreshold;
          prevThreshold = z.threshold;
          
          // Generate a color based on index
          const hue = (idx * 60) % 360;
          return (
              <div 
                key={z.id} 
                style={{ width: `${width}%`, backgroundColor: `hsl(${hue}, 70%, 50%)` }}
                className="h-full first:rounded-l-full last:rounded-r-full relative group"
                title={`${start}% - ${z.threshold}%: ${z.soundType === 'SOUND_EFFECT' ? z.soundEffect : 'Custom/Melody'}`}
              >
                  {width > 15 && <span className="absolute inset-0 flex items-center justify-center text-[7px] font-black text-white drop-shadow-md truncate px-1">{z.soundType === 'SOUND_EFFECT' ? z.soundEffect : 'Sound'}</span>}
              </div>
          );
      });

      // Fill remaining if last < 100
      if (prevThreshold < 100) {
           const lastZ = sortedZones[sortedZones.length-1];
           const width = 100 - prevThreshold;
           // The logic uses the last defined zone for anything above its threshold
           const hue = ((sortedZones.length - 1) * 60) % 360;
           bars.push(
               <div 
                 key="filler" 
                 style={{ width: `${width}%`, backgroundColor: `hsl(${hue}, 70%, 50%)`, opacity: 0.5 }}
                 className="h-full last:rounded-r-full relative"
                 title={`${prevThreshold}% - 100%: ${lastZ.soundType === 'SOUND_EFFECT' ? lastZ.soundEffect : 'Custom/Melody'} (Extended)`}
               >
                   <span className="absolute inset-0 flex items-center justify-center text-[7px] font-black text-white/80 drop-shadow-md">Extended</span>
               </div>
           );
      }

      return <div className="w-full h-5 flex rounded-full bg-black/10 mt-2 mb-2">{bars}</div>;
  };

  return (
    <div className="flex flex-col gap-3 p-4 rounded-3xl border bg-orange-500/5 border-orange-500/20 mb-4">
      <div className="flex items-center justify-between">
         <SectionLabel icon="fa-layer-group" darkMode={config.darkMode}>Progressive Break Stages</SectionLabel>
         <Toggle active={config.enableBreakSoundZones} onClick={() => setConfig(p => ({...p, enableBreakSoundZones: !p.enableBreakSoundZones}))} color="bg-orange-600" />
      </div>

      <div className={`flex flex-col gap-2 transition-all ${config.enableBreakSoundZones ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
          <div className="text-[9px] opacity-60 italic mb-1">
             Define sounds that play based on % of total rings destroyed.
             Example: "Up To 30%" plays for the first 30% of rings.
          </div>
          
          {renderVisualizer()}

          {/* PRESET MANAGER */}
          <div className={`flex flex-col gap-2 p-2 rounded-xl mb-2 border ${config.darkMode ? 'bg-neutral-800/80 border-neutral-700' : 'bg-white/80 border-neutral-200'}`}>
              <span className="text-[8px] font-black uppercase opacity-50">Zone Presets</span>
              <div className="flex gap-2">
                  <input 
                    type="text" 
                    value={presetName} 
                    onChange={e => setPresetName(e.target.value)} 
                    placeholder="Preset Name..." 
                    className={`flex-1 px-2 py-1 text-[9px] border rounded-lg outline-none ${config.darkMode ? 'bg-neutral-900 border-neutral-600' : 'bg-neutral-100 border-neutral-300'}`}
                  />
                  <button onClick={savePreset} className="px-3 bg-orange-600 text-white rounded-lg text-[9px] font-bold">SAVE</button>
              </div>
              {savedPresets.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1 max-h-20 overflow-y-auto custom-scrollbar">
                      {savedPresets.map(p => (
                          <div key={p.id} className="flex items-center gap-1 bg-orange-500/10 border border-orange-500/20 px-2 py-1 rounded-md">
                              <span className="text-[8px] font-bold text-orange-600 cursor-pointer hover:underline" onClick={() => loadPreset(p)}>{p.name}</span>
                              <i onClick={() => deletePreset(p.id)} className="fa-solid fa-times text-[8px] text-orange-400 hover:text-red-500 cursor-pointer ml-1"></i>
                          </div>
                      ))}
                  </div>
              )}
          </div>

          {sortedZones.map((zone, idx) => (
              <div key={zone.id} className={`flex flex-col gap-2 p-2 rounded-xl border relative overflow-hidden ${config.darkMode ? 'bg-neutral-800/50 border-neutral-700' : 'bg-white border-neutral-200'}`}>
                  {/* Color Strip Indicator */}
                  <div className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: `hsl(${(idx * 60) % 360}, 70%, 50%)` }} />
                  
                  <div className="flex items-center gap-2 pl-2">
                      <div className="w-5 h-5 rounded-full bg-neutral-500/10 flex items-center justify-center text-[9px] font-black">{idx + 1}</div>
                      <div className="flex-1 flex items-center gap-2">
                          <span className="text-[9px] font-black uppercase">Until</span>
                          <input 
                            type="number" min={1} max={100} 
                            value={zone.threshold} 
                            onChange={e => {
                                let val = parseInt(e.target.value);
                                if (isNaN(val)) val = 0;
                                if (val > 100) val = 100;
                                updateZone(zone.id, { threshold: val });
                            }}
                            className={`w-12 px-1 py-0.5 text-[9px] font-mono text-center border rounded outline-none ${config.darkMode ? 'bg-neutral-900 border-neutral-600 text-white' : 'bg-neutral-100 border-neutral-300'}`}
                          />
                          <span className="text-[9px] font-black uppercase">% Gone</span>
                      </div>
                      <button onClick={() => playPreview(zone)} className="w-6 h-6 rounded-md bg-indigo-500/10 text-indigo-500 flex items-center justify-center text-[9px] hover:bg-indigo-500/20"><i className="fa-solid fa-play"></i></button>
                      <button onClick={() => removeZone(zone.id)} className="w-6 h-6 rounded-md bg-red-500/10 text-red-500 flex items-center justify-center text-[9px] hover:bg-red-500/20"><i className="fa-solid fa-times"></i></button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 pl-2">
                      <select 
                        value={zone.soundType} 
                        onChange={e => updateZone(zone.id, { soundType: e.target.value as BallCollisionSoundType })}
                        className={`w-full px-2 py-1 border rounded-lg text-[9px] font-black outline-none ${config.darkMode ? 'bg-neutral-900 border-neutral-600 text-white' : 'bg-white border-neutral-200'}`}
                      >
                          <option value="SOUND_EFFECT">FX</option>
                          <option value="CUSTOM">User</option>
                      </select>
                      
                      {zone.soundType === 'SOUND_EFFECT' ? (
                          <select 
                            value={zone.soundEffect} 
                            onChange={e => updateZone(zone.id, { soundEffect: e.target.value as BallSoundEffect })}
                            className={`w-full px-2 py-1 border rounded-lg text-[9px] font-black outline-none ${config.darkMode ? 'bg-neutral-900 border-neutral-600 text-white' : 'bg-white border-neutral-200'}`}
                          >
                              {soundEffects.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                      ) : (
                          <select 
                            value={zone.customSoundId || ''} 
                            onChange={e => updateZone(zone.id, { customSoundId: e.target.value })}
                            className={`w-full px-2 py-1 border rounded-lg text-[9px] font-black outline-none ${config.darkMode ? 'bg-neutral-900 border-neutral-600 text-white' : 'bg-white border-neutral-200'}`}
                          >
                              <option value="">Select Upload...</option>
                              {config.userSounds.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                      )}
                  </div>
              </div>
          ))}

          <button onClick={addZone} className="w-full py-2 bg-neutral-500/10 text-neutral-500 rounded-xl font-black text-[9px] uppercase hover:bg-neutral-500/20 border border-dashed border-neutral-500/30 flex items-center justify-center gap-2">
             <i className="fa-solid fa-plus"></i> Add Sound Stage
          </button>
      </div>
    </div>
  );
};